import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { Reveal } from "@/components/invite/Reveal";
import lacre from "@/assets/invitacion/lacre.png";
import rama1 from "@/assets/invitacion/rama-1.png";
import rama2 from "@/assets/invitacion/rama-2.png";
import rama3 from "@/assets/invitacion/rama-3.png";

const FLAP_MS = 1150;

const BRANCHES = [
  { src: rama1, className: "-left-24 top-[38%] w-[26rem] -translate-y-1/2 rotate-[-6deg]" },
  // Las dos de la derecha se tocaban por el centro. Subo una y bajo la otra
  // hasta que solo asoman por sus esquinas.
  { src: rama2, className: "-right-20 -top-28 w-[22rem] rotate-[168deg]" },
  // En móvil esta se cruza con el aviso de deslizar, así que solo aparece a
  // partir de tablet.
  { src: rama3, className: "hidden md:block -right-10 -bottom-72 w-[17rem] rotate-[28deg] opacity-70" },
];

export default function EnvelopeHero({ invite, standalone = false }) {
  const { family_name, passes, wedding, content } = invite;
  const t = content.texts;

  const [open, setOpen] = useState(false);
  // Pasada la vertical, la solapa deja de tapar la boca y se coloca detrás.
  const [flapBehind, setFlapBehind] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(() => setFlapBehind(true), FLAP_MS * 0.5);
    return () => clearTimeout(id);
  }, [open]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 42, damping: 18 });
  const sy = useSpring(my, { stiffness: 42, damping: 18 });
  // Un par por rama, declarados de uno en uno: los hooks no pueden vivir
  // dentro del map que las pinta.
  const drift1X = useTransform(sx, [-1, 1], [-22, 22]);
  const drift1Y = useTransform(sy, [-1, 1], [-15, 15]);
  const drift2X = useTransform(sx, [-1, 1], [15, -15]);
  const drift2Y = useTransform(sy, [-1, 1], [11, -11]);
  const drift3X = useTransform(sx, [-1, 1], [-10, 10]);
  const drift3Y = useTransform(sy, [-1, 1], [-7, 7]);
  const drifts = [
    { x: drift1X, y: drift1Y },
    { x: drift2X, y: drift2Y },
    { x: drift3X, y: drift3Y },
  ];

  const { scrollY } = useScroll();
  const sceneY = useTransform(scrollY, [0, 600], [0, -90]);
  const sceneOpacity = useTransform(scrollY, [0, 520], [1, 0.15]);

  const onMove = (e) => {
    mx.set((e.clientX / window.innerWidth) * 2 - 1);
    my.set((e.clientY / window.innerHeight) * 2 - 1);
  };

  return (
    <section
      className="relative min-h-screen overflow-hidden bg-cream flex flex-col"
      onMouseMove={onMove}
      data-testid="envelope-hero"
    >
      <div className="invite-paper" aria-hidden="true" />

      {BRANCHES.map((branch, i) => (
        <motion.img
          key={branch.src}
          src={branch.src}
          alt=""
          aria-hidden="true"
          className={`pointer-events-none absolute z-[1] opacity-30 ${branch.className}`}
          style={drifts[i]}
        />
      ))}

      <header className="relative z-10 flex flex-col items-center gap-1 px-6 pt-8 text-center sm:flex-row sm:items-center sm:justify-between sm:px-12 sm:text-left">
        <Reveal delay={0.3}>
          <span className="overline-label text-taupe">{wedding.couple}</span>
        </Reveal>
        <Reveal delay={0.4}>
          <span className="overline-label text-bronze">{wedding.date_label}</span>
        </Reveal>
      </header>

      <motion.div
        className="relative z-10 flex flex-1 flex-col items-center justify-center gap-5 px-6 py-6"
        style={{ y: sceneY, opacity: sceneOpacity }}
      >
        <motion.div
          className="flex flex-col items-center gap-3"
          animate={{ opacity: open ? 0 : 1 }}
          transition={{ duration: open ? 0.4 : 0.6, ease: "easeOut" }}
        >
          <Reveal delay={0.55}>
            <span
              className="block font-serif uppercase text-base sm:text-lg font-light tracking-[0.34em] text-taupe"
              data-testid="hero-overline"
            >
              {t.hero_overline}
            </span>
          </Reveal>
          <motion.span
            className="h-px w-12 bg-bronze"
            aria-hidden="true"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
          />
        </motion.div>

        <div className="envelope-scene">
          {/* Cerrada tapa la boca; al abrirse gira sobre su borde superior y
              termina asomando por detrás. */}
          <motion.div
            className="envelope-flap"
            aria-hidden="true"
            style={{ zIndex: flapBehind ? 1 : 5 }}
            initial={{ rotateX: 0 }}
            animate={{ rotateX: open ? -180 : 0 }}
            transition={{ duration: FLAP_MS / 1000, ease: [0.66, 0, 0.34, 1] }}
          />

          {/* Interior del sobre. Recorta la tarjeta mientras está guardada. */}
          <div className="envelope-mouth">
            <div className="envelope-face envelope-face--left" aria-hidden="true" />
            <div className="envelope-face envelope-face--right" aria-hidden="true" />
            <motion.div
              className="envelope-card"
              initial={{ y: "175%" }}
              animate={{ y: open ? "0%" : "175%" }}
              transition={{ duration: 1.2, delay: open ? 0.75 : 0, ease: [0.22, 1, 0.36, 1] }}
              aria-hidden={!open}
              data-testid="envelope-card"
            >
              <Reveal delay={0.7}>
                <h1 className="card-name" data-testid="hero-family-name">
                  {family_name}
                </h1>
              </Reveal>
              <Reveal delay={0.85}>
                <p className="card-lead">
                  {t.hero_lead}{" "}
                  <strong className="font-semibold text-ink">
                    {wedding.bride} y {wedding.groom}
                  </strong>
                </p>
              </Reveal>
              <Reveal delay={1}>
                <p className="card-meta">{t.hero_passes}</p>
              </Reveal>
              <Reveal delay={1.1}>
                <p className="card-passes" data-testid="hero-passes-count">
                  {passes} {passes === 1 ? "pase" : "pases"}
                </p>
              </Reveal>
              <Reveal delay={1.3}>
                <a
                  href={wedding.open_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  tabIndex={open ? 0 : -1}
                  className="card-cta inline-block bg-[#E3DBCE] text-taupe transition-colors duration-300 hover:bg-nude hover:text-ink"
                  data-testid="open-invitation-btn"
                >
                  {t.hero_cta}
                </a>
              </Reveal>
            </motion.div>
          </div>

          {/* Aletas dobladas, por delante de la tarjeta. */}
          <div className="envelope-front" aria-hidden="true">
            <div className="envelope-face envelope-face--bottom" />
            <div className="envelope-edges" />
          </div>

          <button
            type="button"
            onClick={() => setOpen(true)}
            disabled={open}
            aria-label={open ? "Invitación a la vista" : "Ver la invitación"}
            className="absolute inset-0 z-[7] border-0 bg-transparent p-0 disabled:pointer-events-none"
            style={{ cursor: open ? "default" : "pointer" }}
            data-testid="envelope-flap"
          />

          <motion.img
            src={lacre}
            alt=""
            aria-hidden="true"
            className="envelope-seal"
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.45, ease: [0.34, 1.56, 0.64, 1] }}
            data-testid="envelope-seal"
          />
        </div>
      </motion.div>

      <div className="relative z-10 flex flex-col items-center gap-2 pb-8">
        {/* Sin secciones debajo no hay nada a lo que bajar. */}
        {open && standalone ? null : open ? (
          <motion.div
            className="flex flex-col items-center gap-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6, duration: 1 }}
            data-testid="scroll-cue"
          >
            <span className="overline-label text-taupe">{t.hero_scroll}</span>
            <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}>
              <ArrowDown className="h-4 w-4 text-bronze" strokeWidth={1.5} />
            </motion.span>
          </motion.div>
        ) : (
          <motion.span
            className="overline-label text-bronze"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0.45, 1] }}
            transition={{ delay: 1.4, duration: 2.4, times: [0, 0.3, 0.65, 1] }}
            data-testid="open-hint"
          >
            {t.hero_hint}
          </motion.span>
        )}
      </div>
    </section>
  );
}
